import React, { useState, useEffect } from 'react';
import { Select, Button } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { medicalReportService } from '../../services/medicalReportService';
import { patientService } from '../../services';
import './MedicalReport.css';

function MedicalReport() {
    const { t } = useTranslation();
    const [reports, setReports] = useState([]);
    const [patients, setPatients] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editingReport, setEditingReport] = useState(null);
    const [form, setForm] = useState({
        patientId: '',
        reportType: 'general',
        title: '',
        chiefComplaint: '',
        presentIllness: '',
        medicalHistory: '',
        physicalExamination: '',
        vitalSigns: {
            bloodPressure: '',
            temperature: '',
            pulse: '',
            respiratoryRate: '',
            weight: '',
            height: '',
            oxygenSaturation: ''
        },
        diagnosis: '',
        differentialDiagnosis: '',
        treatmentPlan: '',
        recommendations: '',
        followUpDate: '',
        prognosis: 'good',
        doctorNotes: '',
        isConfidential: false,
        status: 'final'
    });

    useEffect(() => {
        loadReports();
        loadPatients();
    }, []);

    const loadPatients = async () => {
        try {
            const response = await patientService.getPatients();
            setPatients(response.data || []);
        } catch (error) {
            console.error('Error loading patients:', error);
        }
    };

    const loadReports = async () => {
        try {
            const data = await medicalReportService.getReports();
            setReports(data);
        } catch (error) {
            console.error('Error loading medical reports:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate required fields
        if (!form.patientId) {
            alert(t('pleaseSelectPatient') || 'Please select a patient');
            return;
        }

        if (!form.title) {
            alert(t('pleaseFillTitle') || 'Please fill in the title');
            return;
        }

        if (!form.diagnosis) {
            alert(t('pleaseFillDiagnosis') || 'Please fill in the diagnosis');
            return;
        }

        try {
            if (editingReport) {
                await medicalReportService.updateReport(editingReport._id, form);
            } else {
                await medicalReportService.createReport(form);
            }
            setShowModal(false);
            resetForm();
            loadReports();
        } catch (error) {
            alert('Error saving medical report: ' + error.message);
        }
    };

    const resetForm = () => {
        setForm({
            patientId: '',
            reportType: 'general',
            title: '',
            chiefComplaint: '',
            presentIllness: '',
            medicalHistory: '',
            physicalExamination: '',
            vitalSigns: {
                bloodPressure: '',
                temperature: '',
                pulse: '',
                respiratoryRate: '',
                weight: '',
                height: '',
                oxygenSaturation: ''
            },
            diagnosis: '',
            differentialDiagnosis: '',
            treatmentPlan: '',
            recommendations: '',
            followUpDate: '',
            prognosis: 'good',
            doctorNotes: '',
            isConfidential: false,
            status: 'final'
        });
        setEditingReport(null);
    };

    const handleEdit = (report) => {
        setEditingReport(report);
        setForm({
            patientId: report.patient._id,
            reportType: report.reportType,
            title: report.title,
            chiefComplaint: report.chiefComplaint || '',
            presentIllness: report.presentIllness || '',
            medicalHistory: report.medicalHistory || '',
            physicalExamination: report.physicalExamination || '',
            vitalSigns: report.vitalSigns || {
                bloodPressure: '',
                temperature: '',
                pulse: '',
                respiratoryRate: '',
                weight: '',
                height: '',
                oxygenSaturation: ''
            },
            diagnosis: report.diagnosis,
            differentialDiagnosis: report.differentialDiagnosis || '',
            treatmentPlan: report.treatmentPlan || '',
            recommendations: report.recommendations || '',
            followUpDate: report.followUpDate ? new Date(report.followUpDate).toISOString().split('T')[0] : '',
            prognosis: report.prognosis,
            doctorNotes: report.doctorNotes || '',
            isConfidential: report.isConfidential,
            status: report.status
        });
        setShowModal(true);
    };

    const handleDelete = async (reportId) => {
        if (window.confirm(t('confirmDelete') || 'Are you sure you want to delete this report?')) {
            try {
                await medicalReportService.deleteReport(reportId);
                loadReports();
            } catch (error) {
                alert('Error deleting report: ' + error.message);
            }
        }
    };

    const printReport = (report) => {
        const printWindow = window.open('', '', 'width=800,height=600');
        printWindow.document.write(`
      <html>
        <head>
          <title>${t('medicalReport')} - ${report.reportNumber}</title>
          <style>
            @page {
                size: A4;
                margin: 0;
            }
            * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
            }
            body { 
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                line-height: 1.4;
                color: #333;
                background: white;
                width: 210mm;
                height: 297mm;
                padding: 0;
                margin: 0 auto;
            }
            .header {
                background: linear-gradient(135deg, #3e2723 0%, #5d4037 100%);
                padding: 12px 20px;
                color: white;
                position: relative;
                overflow: hidden;
            }
            .header::after {
                content: '';
                position: absolute;
                top: 0;
                right: 0;
                width: 120px;
                height: 100%;
                background: white;
                transform: skewX(-15deg);
                transform-origin: top right;
                margin-right: -60px;
                opacity: 0.1;
            }
            .logo {
                width: 35px;
                height: 35px;
                background: #ff9800;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                font-size: 18px;
                font-weight: bold;
                margin-bottom: 3px;
                border-radius: 4px;
            }
            .clinic-name {
                font-size: 15px;
                font-weight: 600;
                margin-bottom: 2px;
            }
            .contact-info {
                font-size: 8px;
                opacity: 0.9;
                margin-top: 2px;
            }
            .contact-info div {
                margin: 1px 0;
            }
            .report-title {
                text-align: center;
                font-size: 14px;
                font-weight: 700;
                color: #3e2723;
                margin: 10px 0 8px 0;
                text-transform: uppercase;
                letter-spacing: 1.2px;
            }
            .content {
                padding: 0 20px 15px 20px;
            }
            .report-number {
                text-align: right;
                font-size: 9px;
                color: #666;
                margin-bottom: 8px;
                font-weight: 600;
            }
            .badge {
                display: inline-block;
                padding: 2px 8px;
                border-radius: 10px;
                font-size: 8px;
                font-weight: 600;
                margin-left: 5px;
            }
            .badge.final { background: #c8e6c9; color: #2e7d32; }
            .badge.draft { background: #fff9c4; color: #f57f17; }
            .badge.confidential { background: #ffcdd2; color: #c62828; }
            .section {
                margin-bottom: 8px;
            }
            .section-title {
                font-size: 9px;
                color: #3e2723;
                font-weight: 700;
                text-transform: uppercase;
                margin-bottom: 4px;
                border-bottom: 2px solid #ff9800;
                padding-bottom: 2px;
            }
            .info-grid {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 6px;
                margin-bottom: 8px;
            }
            .info-item {
                font-size: 9px;
            }
            .info-label {
                color: #666;
                font-size: 8px;
                text-transform: uppercase;
                letter-spacing: 0.3px;
                margin-bottom: 1px;
            }
            .info-value {
                color: #333;
                font-weight: 600;
                font-size: 10px;
            }
            .full-width {
                grid-column: 1 / -1;
            }
            .section-content {
                font-size: 9px;
                color: #333;
                line-height: 1.3;
                background: #fafafa;
                padding: 6px;
                border-radius: 3px;
            }
            .vital-signs {
                display: grid;
                grid-template-columns: repeat(4, 1fr);
                gap: 6px;
                background: #f9f9f9;
                padding: 8px;
                border-radius: 4px;
            }
            .vital-item {
                text-align: center;
            }
            .vital-label {
                font-size: 7px;
                color: #666;
                text-transform: uppercase;
            }
            .vital-value {
                font-size: 10px;
                font-weight: 700;
                color: #3e2723;
                margin-top: 2px;
            }
            .diagnosis-box {
                background: #f5f5f5;
                padding: 6px;
                border-radius: 3px;
                border-left: 3px solid #ff9800;
                margin: 4px 0;
            }
            .diagnosis-text {
                font-size: 10px;
                color: #333;
                line-height: 1.3;
                font-weight: 600;
            }
            .signature-area {
                margin-top: 10px;
                padding-top: 8px;
                border-top: 1px solid #e0e0e0;
            }
            .signature-box {
                text-align: right;
            }
            .signature-line {
                border-bottom: 2px solid #333;
                width: 160px;
                margin: 12px 0 4px auto;
            }
            .doctor-name {
                font-weight: 700;
                font-size: 10px;
                color: #3e2723;
            }
            .doctor-title {
                font-size: 8px;
                color: #666;
                margin-top: 1px;
            }
            .footer {
                margin-top: 8px;
                text-align: center;
                font-size: 7px;
                color: #999;
                padding-top: 6px;
                border-top: 1px solid #eee;
            }
            @media print {
                body {
                    width: 210mm;
                    height: 297mm;
                }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="logo">Dr</div>
            <div class="clinic-name">${report.doctor?.clinicName || report.doctor?.name || 'Medical Clinic'}</div>
            <div class="contact-info">
              <div>${report.doctor?.specialization || 'Medical Practice'}</div>
              ${report.doctor?.phoneNumber ? `<div>📞 ${report.doctor.phoneNumber}</div>` : ''}
              ${report.doctor?.email ? `<div>📧 ${report.doctor.email}</div>` : ''}
            </div>
          </div>
          
          <div class="content">
            <div class="report-title">${t('medicalReport')}</div>

            <div class="report-number">
              ${t('reportNumber')}: ${report.reportNumber}
              <span class="badge ${report.status}">${t(report.status) || report.status}</span>
              ${report.isConfidential ? `<span class="badge confidential">${t('confidential')}</span>` : ''}
              <br/>
              ${t('reportDate')}: ${new Date(report.reportDate).toLocaleDateString('en-GB')}
            </div>

            <div class="section">
              <div class="section-title">${t('patientName')} & Report Information</div>
              <div class="info-grid">
                <div class="info-item">
                  <div class="info-label">${t('patientName')}</div>
                  <div class="info-value">${report.patient?.name || 'N/A'}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">${t('age')} / ${t('gender')}</div>
                  <div class="info-value">${report.patient?.age || ''} ${t('years')} / ${t(report.patient?.gender) || report.patient?.gender || ''}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">${t('phone')}</div>
                  <div class="info-value">${report.patient?.phone || 'N/A'}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">${t('reportType')}</div>
                  <div class="info-value" style="text-transform: capitalize;">${t(report.reportType) || report.reportType}</div>
                </div>
                ${report.title ? `
                <div class="info-item full-width">
                  <div class="info-label">${t('title')}</div>
                  <div class="info-value">${report.title}</div>
                </div>
                ` : ''}
              </div>
            </div>

            ${report.chiefComplaint ? `
            <div class="section">
              <div class="section-title">${t('chiefComplaint')}</div>
              <div class="section-content">${report.chiefComplaint}</div>
            </div>
            ` : ''}

            ${report.presentIllness ? `
            <div class="section">
              <div class="section-title">${t('presentIllness')}</div>
              <div class="section-content">${report.presentIllness}</div>
            </div>
            ` : ''}

            ${report.medicalHistory ? `
            <div class="section">
              <div class="section-title">${t('medicalHistory')}</div>
              <div class="section-content">${report.medicalHistory}</div>
            </div>
            ` : ''}

            ${report.vitalSigns && (report.vitalSigns.bloodPressure || report.vitalSigns.temperature || report.vitalSigns.pulse || report.vitalSigns.weight) ? `
            <div class="section">
              <div class="section-title">${t('vitalSigns')}</div>
              <div class="vital-signs">
                ${report.vitalSigns.bloodPressure ? `<div class="vital-item"><div class="vital-label">${t('bloodPressure')}</div><div class="vital-value">${report.vitalSigns.bloodPressure}</div></div>` : ''}
                ${report.vitalSigns.temperature ? `<div class="vital-item"><div class="vital-label">${t('temperature')}</div><div class="vital-value">${report.vitalSigns.temperature}°</div></div>` : ''}
                ${report.vitalSigns.pulse ? `<div class="vital-item"><div class="vital-label">${t('pulse')}</div><div class="vital-value">${report.vitalSigns.pulse}</div></div>` : ''}
                ${report.vitalSigns.respiratoryRate ? `<div class="vital-item"><div class="vital-label">${t('respiratoryRate')}</div><div class="vital-value">${report.vitalSigns.respiratoryRate}</div></div>` : ''}
                ${report.vitalSigns.weight ? `<div class="vital-item"><div class="vital-label">${t('weight')}</div><div class="vital-value">${report.vitalSigns.weight} kg</div></div>` : ''}
                ${report.vitalSigns.height ? `<div class="vital-item"><div class="vital-label">${t('height')}</div><div class="vital-value">${report.vitalSigns.height} cm</div></div>` : ''}
              </div>
            </div>
            ` : ''}

            ${report.physicalExamination ? `
            <div class="section">
              <div class="section-title">${t('physicalExamination')}</div>
              <div class="section-content">${report.physicalExamination}</div>
            </div>
            ` : ''}

            <div class="section">
              <div class="section-title">${t('diagnosis')}</div>
              <div class="diagnosis-box">
                <div class="diagnosis-text">${report.diagnosis}</div>
              </div>
            </div>

            ${report.differentialDiagnosis ? `
            <div class="section">
              <div class="section-title">${t('differentialDiagnosis')}</div>
              <div class="section-content">${report.differentialDiagnosis}</div>
            </div>
            ` : ''}

            ${report.treatmentPlan ? `
            <div class="section">
              <div class="section-title">${t('treatmentPlan')}</div>
              <div class="section-content">${report.treatmentPlan}</div>
            </div>
            ` : ''}

            ${report.recommendations ? `
            <div class="section">
              <div class="section-title">${t('recommendations')}</div>
              <div class="section-content">${report.recommendations}</div>
            </div>
            ` : ''}

            ${report.followUpDate ? `
            <div class="section">
              <div class="section-title">${t('followUpDate')}</div>
              <div class="section-content">${new Date(report.followUpDate).toLocaleDateString('en-GB')}</div>
            </div>
            ` : ''}

            <div class="signature-area">
              <div class="signature-box">
                <div class="signature-line"></div>
                <div class="doctor-name">${report.doctor?.name || ''}</div>
                <div class="doctor-title">${report.doctor?.specialization || ''}</div>
              </div>
            </div>

            <div class="footer">
              ${t('generatedOn')} ${new Date().toLocaleString()} | ${t('medicalReportSystem')}
            </div>
          </div>
        </body>
      </html>
    `);
        printWindow.document.close();
        printWindow.print();
    };

    const reportTypeOptions = [
        { value: 'general', label: t('general') || 'General' },
        { value: 'specialist', label: t('specialist') || 'Specialist' },
        { value: 'laboratory', label: t('laboratory') || 'Laboratory' },
        { value: 'radiology', label: t('radiology') || 'Radiology' },
        { value: 'surgical', label: t('surgical') || 'Surgical' },
        { value: 'discharge', label: t('discharge') || 'Discharge' },
        { value: 'consultation', label: t('consultation') || 'Consultation' }
    ];

    const prognosisOptions = [
        { value: 'excellent', label: t('excellent') || 'Excellent' },
        { value: 'good', label: t('good') || 'Good' },
        { value: 'fair', label: t('fair') || 'Fair' },
        { value: 'guarded', label: t('guarded') || 'Guarded' },
        { value: 'poor', label: t('poor') || 'Poor' }
    ];

    const statusOptions = [
        { value: 'draft', label: t('draft') || 'Draft' },
        { value: 'final', label: t('final') || 'Final' },
        { value: 'amended', label: t('amended') || 'Amended' }
    ];

    return (
        <div className="medicalreport-container">
            <div className="page-header">
                <h1>{t('medicalReports')}</h1>
                <Button
                    type="primary"
                    icon={<FileTextOutlined />}
                    onClick={() => { setShowModal(true); resetForm(); }}
                    className="btn btn-primary"
                >
                    {t('createMedicalReport')}
                </Button>
            </div>

            <div className="reports-list">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>{t('reportNumber')}</th>
                            <th>{t('patient')}</th>
                            <th>{t('reportType')}</th>
                            <th>{t('title')}</th>
                            <th>{t('reportDate')}</th>
                            <th>{t('status')}</th>
                            <th>{t('actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {reports.map(report => (
                            <tr key={report._id}>
                                <td>{report.reportNumber}</td>
                                <td>{report.patient?.name}</td>
                                <td>
                                    <span className={`badge ${report.reportType}`}>
                                        {t(report.reportType) || report.reportType}
                                    </span>
                                </td>
                                <td>{report.title}</td>
                                <td>{new Date(report.reportDate).toLocaleDateString()}</td>
                                <td>
                                    <span className={`badge ${report.status}`}>
                                        {t(report.status) || report.status}
                                    </span>
                                </td>
                                <td>
                                    <button
                                        className="btn-sm"
                                        onClick={() => printReport(report)}
                                        style={{ marginRight: '5px' }}
                                    >
                                        {t('print')}
                                    </button>
                                    <button
                                        className="btn-sm"
                                        onClick={() => handleEdit(report)}
                                        style={{ marginRight: '5px' }}
                                    >
                                        {t('edit')}
                                    </button>
                                    <button
                                        className="btn-sm btn-danger"
                                        onClick={() => handleDelete(report._id)}
                                    >
                                        {t('delete')}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingReport ? t('editMedicalReport') : t('createMedicalReport')}</h2>
                            <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
                        </div>
                        <form onSubmit={handleSubmit} className="modal-body">
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>{t('patient')} *</label>
                                    <Select
                                        showSearch
                                        value={form.patientId}
                                        onChange={(value) => setForm({ ...form, patientId: value })}
                                        filterOption={(input, option) =>
                                            option.children.toLowerCase().includes(input.toLowerCase())
                                        }
                                        required
                                        style={{ width: '100%' }}
                                    >
                                        {patients.map(patient => (
                                            <Select.Option key={patient._id} value={patient._id}>
                                                {patient.name} - {patient.phone}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </div>

                                <div className="form-group">
                                    <label>{t('reportType')} *</label>
                                    <select
                                        value={form.reportType}
                                        onChange={(e) => setForm({ ...form, reportType: e.target.value })}
                                        required
                                    >
                                        {reportTypeOptions.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group full-width">
                                    <label>{t('title')} *</label>
                                    <input
                                        type="text"
                                        value={form.title}
                                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                                        required
                                    />
                                </div>

                                <div className="form-group full-width">
                                    <label>{t('chiefComplaint')}</label>
                                    <textarea
                                        value={form.chiefComplaint}
                                        onChange={(e) => setForm({ ...form, chiefComplaint: e.target.value })}
                                        rows="2"
                                    />
                                </div>

                                <div className="form-group full-width">
                                    <label>{t('presentIllness')}</label>
                                    <textarea
                                        value={form.presentIllness}
                                        onChange={(e) => setForm({ ...form, presentIllness: e.target.value })}
                                        rows="3"
                                    />
                                </div>

                                <div className="form-group full-width">
                                    <label>{t('medicalHistory')}</label>
                                    <textarea
                                        value={form.medicalHistory}
                                        onChange={(e) => setForm({ ...form, medicalHistory: e.target.value })}
                                        rows="2"
                                    />
                                </div>

                                <div className="vital-signs-section full-width">
                                    <h3>{t('vitalSigns')}</h3>
                                    <div className="vital-signs-grid">
                                        <div className="form-group">
                                            <label>{t('bloodPressure')}</label>
                                            <input
                                                type="text"
                                                placeholder="120/80"
                                                value={form.vitalSigns.bloodPressure}
                                                onChange={(e) => setForm({
                                                    ...form,
                                                    vitalSigns: { ...form.vitalSigns, bloodPressure: e.target.value }
                                                })}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>{t('temperature')}</label>
                                            <input
                                                type="text"
                                                placeholder="37"
                                                value={form.vitalSigns.temperature}
                                                onChange={(e) => setForm({
                                                    ...form,
                                                    vitalSigns: { ...form.vitalSigns, temperature: e.target.value }
                                                })}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>{t('pulse')}</label>
                                            <input
                                                type="text"
                                                placeholder="72"
                                                value={form.vitalSigns.pulse}
                                                onChange={(e) => setForm({
                                                    ...form,
                                                    vitalSigns: { ...form.vitalSigns, pulse: e.target.value }
                                                })}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>{t('respiratoryRate')}</label>
                                            <input
                                                type="text"
                                                placeholder="16"
                                                value={form.vitalSigns.respiratoryRate}
                                                onChange={(e) => setForm({
                                                    ...form,
                                                    vitalSigns: { ...form.vitalSigns, respiratoryRate: e.target.value }
                                                })}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>{t('weight')} (kg)</label>
                                            <input
                                                type="text"
                                                placeholder="70"
                                                value={form.vitalSigns.weight}
                                                onChange={(e) => setForm({
                                                    ...form,
                                                    vitalSigns: { ...form.vitalSigns, weight: e.target.value }
                                                })}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>{t('height')} (cm)</label>
                                            <input
                                                type="text"
                                                placeholder="170"
                                                value={form.vitalSigns.height}
                                                onChange={(e) => setForm({
                                                    ...form,
                                                    vitalSigns: { ...form.vitalSigns, height: e.target.value }
                                                })}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="form-group full-width">
                                    <label>{t('physicalExamination')}</label>
                                    <textarea
                                        value={form.physicalExamination}
                                        onChange={(e) => setForm({ ...form, physicalExamination: e.target.value })}
                                        rows="3"
                                    />
                                </div>

                                <div className="form-group full-width">
                                    <label>{t('diagnosis')} *</label>
                                    <textarea
                                        value={form.diagnosis}
                                        onChange={(e) => setForm({ ...form, diagnosis: e.target.value })}
                                        required
                                        rows="3"
                                    />
                                </div>

                                <div className="form-group full-width">
                                    <label>{t('differentialDiagnosis')}</label>
                                    <textarea
                                        value={form.differentialDiagnosis}
                                        onChange={(e) => setForm({ ...form, differentialDiagnosis: e.target.value })}
                                        rows="2"
                                    />
                                </div>

                                <div className="form-group full-width">
                                    <label>{t('treatmentPlan')}</label>
                                    <textarea
                                        value={form.treatmentPlan}
                                        onChange={(e) => setForm({ ...form, treatmentPlan: e.target.value })}
                                        rows="3"
                                    />
                                </div>

                                <div className="form-group full-width">
                                    <label>{t('recommendations')}</label>
                                    <textarea
                                        value={form.recommendations}
                                        onChange={(e) => setForm({ ...form, recommendations: e.target.value })}
                                        rows="3"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>{t('followUpDate')}</label>
                                    <input
                                        type="date"
                                        value={form.followUpDate}
                                        onChange={(e) => setForm({ ...form, followUpDate: e.target.value })}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>{t('prognosis')}</label>
                                    <select
                                        value={form.prognosis}
                                        onChange={(e) => setForm({ ...form, prognosis: e.target.value })}
                                    >
                                        {prognosisOptions.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>{t('status')}</label>
                                    <select
                                        value={form.status}
                                        onChange={(e) => setForm({ ...form, status: e.target.value })}
                                    >
                                        {statusOptions.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group full-width">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={form.isConfidential}
                                            onChange={(e) => setForm({ ...form, isConfidential: e.target.checked })}
                                            style={{ marginRight: '8px' }}
                                        />
                                        {t('confidential')}
                                    </label>
                                </div>

                                <div className="form-group full-width">
                                    <label>{t('doctorNotes')}</label>
                                    <textarea
                                        value={form.doctorNotes}
                                        onChange={(e) => setForm({ ...form, doctorNotes: e.target.value })}
                                        rows="2"
                                        placeholder={t('internalNotesPlaceholder')}
                                    />
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                                    {t('cancel')}
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    {editingReport ? t('update') : t('create')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default MedicalReport;
